// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/pages/sales/settings/ClientVisitDetail.jsx (PART 1 OF 2)
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import FollowUpForm from './FollowUpForm';
import ClientVisitDetailSkeleton from '../SkeletonComponents/ClientVisitDetailSkeleton';
import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import { toast } from 'react-toastify';
import { FaArrowLeftLong, FaArrowRightLong } from 'react-icons/fa6';
import { BsFlag, BsFlagFill } from "react-icons/bs";
import { GraduationCap, Layers, ExternalLink } from 'lucide-react';

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-xs">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative z-10">
        <h3 className="text-base font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-xs"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const ClientVisitDetail = () => {
  const { id, portalName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [salesStages, setSalesStages] = useState([]);
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFollowUpOffcanvas, setShowFollowUpOffcanvas] = useState(false);
  const [stageUpdateModal, setStageUpdateModal] = useState({ isOpen: false, stage: null });
  const [updatingStage, setUpdatingStage] = useState(false);
  const [navLoading, setNavLoading] = useState(false);
  const [priorityModal, setPriorityModal] = useState(false);
  const [priorityLoading, setPriorityLoading] = useState(false);

  const allIds = useMemo(() => {
    return location.state?.allIds || [];
  }, [location.state]);

  const currentIndex = allIds.findIndex((i) => i === id);
  const isAnyModalOpen = showFollowUpOffcanvas || stageUpdateModal.isOpen || priorityModal;

  const handleNext = useCallback(() => {
    if (currentIndex < allIds.length - 1 && !navLoading) {
      setNavLoading(true);
      navigate(`/${portalName}/sales/leads/view/${allIds[currentIndex + 1]}`, {
        state: { allIds }
      });
    }
  }, [currentIndex, allIds, navigate, portalName, navLoading]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0 && !navLoading) {
      setNavLoading(true);
      navigate(`/${portalName}/sales/leads/view/${allIds[currentIndex - 1]}`, {
        state: { allIds }
      });
    }
  }, [currentIndex, allIds, navigate, portalName, navLoading]);

  // 👉 1. Dynamic Stage Fetching based on Division & Vertical:
  // - If divisionId & verticalId present -> fetch stages mapped to both
  // - If only divisionId present -> fetch division stages + shared stages
  // - If none -> fetch all shared stages
  const fetchSalesStages = useCallback(async (divisionId = null, verticalId = null) => {
    try {
      const params = {
        includeInactive: false,
        includeShared: true,
      };

      if (divisionId) params.divisionId = divisionId;
      if (verticalId) params.verticalId = verticalId;

      const response = await api.get(SummaryApi.getAllStages.url, { params });
      if (response.data.success) {
        setSalesStages(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch stages:', error);
    }
  }, []);

  // 👉 2. Fetch Visit Details and trigger dynamic stage fetching
  const fetchVisitDetails = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.getOneClientVisit.url(id));
      if (response.data.success) {
        const visitData = response.data.data;
        setVisit(visitData);

        // Extract division and business vertical to fetch filtered stages
        const divId = visitData.divisionId?._id || visitData.divisionId || null;
        const vertId = visitData.businessVerticalId?._id || visitData.businessVerticalId || visitData.ourProductId?._id || visitData.ourProductId || null;

        fetchSalesStages(divId, vertId);
      }
    } catch (error) {
      console.error('Error fetching visit details:', error);
      toast.error('Failed to load visit details');
    } finally {
      setLoading(false);
      setNavLoading(false);
    }
  }, [id, fetchSalesStages]);

  useEffect(() => {
    fetchVisitDetails();
  }, [fetchVisitDetails]);

  // Stage Update Handler
  const handleStageUpdate = async () => {
    if (!stageUpdateModal.stage) return;

    setUpdatingStage(true);
    try {
      const selectedStage = stageUpdateModal.stage;
      const payload = {
        stageId: selectedStage._id,
        remarks: `Stage updated to ${selectedStage.stageName}`
      };
      
      const response = await api.put(SummaryApi.updateVisitStage.url(visit._id), payload);

      if (response.data.success) {
        fetchVisitDetails();
        toast.success('Stage updated successfully');
        setStageUpdateModal({ isOpen: false, stage: null });
      } else {
        toast.error('Failed to update stage');
      }
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error('Failed to update stage');
    } finally {
      setUpdatingStage(false);
    }
  };

  // Keyboard navigation shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (isAnyModalOpen) return;
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleNext, handlePrev, isAnyModalOpen]);

  // Priority Toggle Handler
  const handlePriorityToggle = async () => {
    try {
      setPriorityLoading(true);
      const response = await api.patch(SummaryApi.togglePriority.url(visit._id));

      if (response.data.success) {
        toast.success(response.data.message);
        fetchVisitDetails();
        setPriorityModal(false);
      }
    } catch (error) {
      console.error('Error updating priority:', error);
      toast.error("Failed to update priority");
    } finally {
      setPriorityLoading(false);
    }
  };

  if (loading) {
    return <ClientVisitDetailSkeleton />;
  }

  if (!visit) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Visit not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-blue-600 hover:text-blue-800">
          Go Back
        </button>
      </div>
    );
  }

// ─────────────────────────────────────────────────────────────────────────────
  // PART 2: Complete UI Return Block
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="min-h-screen bg-gray-50 pb-12">
        {/* Header - Mobile Responsive Navigation Bar */}
        <div className="bg-red-100 border-b border-red-200 shadow-xs sticky top-0 z-20">
          <div className="px-4 py-3 max-w-7xl mx-auto">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <button
                  onClick={() => navigate(-1)}
                  className="flex-shrink-0 p-1.5 hover:bg-red-200/60 rounded-lg text-gray-700 transition-colors"
                  title="Go back"
                >
                  <svg
                    className="w-5 h-5 text-gray-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <div className="min-w-0 flex-1">
                  <h1 className="text-sm sm:text-base font-bold text-gray-900 truncate">
                    {visit.clientId}
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    {visit.clientName}
                  </p>
                </div>
              </div>

              {/* Prev / Next Navigation Buttons */}
              <div className="hidden md:flex items-center gap-2">
                <button
                  onClick={handlePrev}
                  disabled={currentIndex === 0 || navLoading}
                  className={`p-2 rounded-md ${
                    currentIndex === 0 || navLoading
                      ? "bg-red-100 text-gray-300 cursor-not-allowed border border-gray-300"
                      : "bg-red-100 hover:bg-white border border-gray-300"
                  }`}
                  title="Previous Lead"
                >
                  {navLoading ? "..." : <FaArrowLeftLong />}
                </button>

                <button
                  onClick={handleNext}
                  disabled={currentIndex === allIds.length - 1 || navLoading}
                  className={`p-2 rounded-lg ${
                    currentIndex === allIds.length - 1 || navLoading
                      ? "bg-red-100 text-gray-300 cursor-not-allowed border border-gray-300"
                      : "bg-red-100 hover:bg-white border border-gray-300"
                  }`}
                  title="Next Lead"
                >
                  {navLoading ? "..." : <FaArrowRightLong />}
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowFollowUpOffcanvas(true)}
                  className="px-3.5 py-1.5 bg-blue-600 text-white text-xs sm:text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap shadow-xs"
                >
                  Follow-up
                </button>
                <Link
                  to={`/${portalName}/sales/leads/edit/${visit._id}`}
                  className="px-3.5 py-1.5 border border-gray-300 bg-white text-gray-700 text-xs sm:text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap shadow-xs"
                >
                  Edit
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="p-4">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
            
            {/* Left 2 Columns: Client Details & Core Cards */}
            <div className="xl:col-span-2 space-y-4 lg:space-y-6">
              
              {/* 1. Client Information Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-blue-100">
                  <div className="flex items-center justify-between">
                    <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
                      <span className="text-lg lg:text-xl mr-2">🏢</span>
                      Client Information
                    </h2>

                    <button
                      onClick={() => setPriorityModal(true)}
                      className="transition-all duration-200 hover:scale-110"
                      title="Priority Follow-up"
                    >
                      {visit?.isPriorityFollowUp ? (
                        <BsFlagFill className="text-red-600 text-2xl" />
                      ) : (
                        <BsFlag className="text-gray-400 text-2xl hover:text-red-500" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="p-4 lg:p-6">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Client Name</dt>
                      <dd className="text-sm lg:text-base text-gray-900 font-bold">{visit.clientName}</dd>
                    </div>

                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Client ID</dt>
                      <dd className="text-sm lg:text-base text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block font-bold">
                        {visit.clientId}
                      </dd>
                    </div>

                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Division</dt>
                      <dd className="text-sm lg:text-base text-gray-900 font-semibold">{visit.divisionId?.name || "-"}</dd>
                    </div>

                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Division Base ID</dt>
                      <dd className="text-sm lg:text-base text-gray-900 font-mono">{visit.divisionBaseId || "-"}</dd>
                    </div>

                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Industry</dt>
                      <dd className="text-sm lg:text-base text-gray-900">
                        <span className="font-semibold">{visit.industryType || "-"}</span>
                        {visit.industrySubType && (
                          <span className="text-gray-600 text-xs lg:text-sm block mt-0.5">
                            {visit.industrySubType}
                          </span>
                        )}
                      </dd>
                    </div>

                    {/* Business Vertical (Common to all divisions) */}
                    <div className="space-y-1 bg-blue-50/70 p-3 rounded-lg border border-blue-200 sm:col-span-2">
                      <dt className="text-xs lg:text-sm font-medium text-blue-800 flex items-center gap-1.5">
                        <Layers className="w-4 h-4 text-blue-600" />
                        Business Vertical
                      </dt>
                      <dd className="text-sm lg:text-base text-gray-900 font-bold flex items-center gap-2 flex-wrap">
                        <span>{visit.businessVerticalId?.name || visit.ourProductId?.name || "Not Set"}</span>
                        {(visit.businessVerticalId?.code || visit.ourProductId?.code) && (
                          <span className="text-xs bg-blue-200/80 text-blue-900 px-2 py-0.5 rounded font-mono font-bold">
                            {visit.businessVerticalId?.code || visit.ourProductId?.code}
                          </span>
                        )}
                      </dd>
                    </div>

                    {/* Source Type Field */}
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Source Type</dt>
                      <dd className="text-sm lg:text-base text-gray-900 flex items-center gap-2">
                        <span className="capitalize font-semibold">{visit.sourceType || "Not Specified"}</span>
                        {visit.sourceType?.toLowerCase() === 'expo' && (
                          <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded border border-orange-200 flex items-center gap-1 font-medium">
                            🎪 {visit.expoPlace} | {visit.expoYear}
                          </span>
                        )}
                      </dd>
                    </div>

                    {/* Product Categories Field */}
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-1">Product Categories</dt>
                      <dd className="flex flex-wrap gap-1.5">
                        {visit.productCategory?.map((cat, index) => (
                          <span key={index} className="px-2.5 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs lg:text-sm font-medium">
                            {cat}
                          </span>
                        )) || "-"}
                      </dd>
                    </div>

                    {/* Requirements Field */}
                    <div className="space-y-1 sm:col-span-2">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-1">Requirements</dt>
                      <dd className="grid grid-cols-1 gap-1">
                        {visit.requirement?.map((req, index) => (
                          <div key={index} className="flex items-center gap-2 text-xs lg:text-sm text-gray-700 font-medium">
                            <span className="text-green-500">●</span> {req}
                          </div>
                        )) || "-"}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* 2. CUSO Division Only: Product, Business Category, & Compliance Details */}
              {(visit.ourProductId || visit.branchCount > 1 || visit.employeeCount || visit.annualTurnover || visit.registrationType === 'registered' || visit.preferredPartnerType || visit.divisionId?.name?.toLowerCase().includes('cuso')) && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-4 py-3 border-b border-purple-100">
                    <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
                      <span className="text-lg lg:text-xl mr-2">💼</span>
                      Product & Business Compliance
                    </h2>
                  </div>

                  <div className="p-4 lg:p-6 space-y-4">
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                      {/* Our ERP Product */}
                      <div className="space-y-1 bg-purple-50/70 p-3 rounded-lg border border-purple-200">
                        <dt className="text-xs lg:text-sm font-medium text-purple-800">Our Product</dt>
                        <dd className="text-sm lg:text-base text-gray-900 font-bold flex items-center gap-2 flex-wrap">
                          <span>{visit.ourProductId?.name || "Not Specified"}</span>
                          {visit.ourProductId?.code && (
                            <span className="text-xs bg-purple-200 text-purple-900 px-2 py-0.5 rounded font-mono font-bold">
                              {visit.ourProductId.code}
                            </span>
                          )}
                        </dd>
                      </div>

                      {/* Product Business Category */}
                      <div className="space-y-1 bg-indigo-50/70 p-3 rounded-lg border border-indigo-200">
                        <dt className="text-xs lg:text-sm font-medium text-indigo-800">Product Business Category</dt>
                        <dd className="text-sm lg:text-base text-gray-900 font-bold flex items-center gap-2 flex-wrap">
                          <span>{visit.ourProductBusinessCategoryId?.name || "Not Specified"}</span>
                          {visit.ourProductBusinessCategoryId?.code && (
                            <span className="text-xs bg-indigo-200 text-indigo-900 px-2 py-0.5 rounded font-mono font-bold">
                              {visit.ourProductBusinessCategoryId.code}
                            </span>
                          )}
                        </dd>
                      </div>

                      {/* Branch Count */}
                      <div className="space-y-1">
                        <dt className="text-xs lg:text-sm font-medium text-gray-500">Branch Count</dt>
                        <dd className="text-sm lg:text-base text-gray-900 font-semibold">{visit.branchCount !== null ? `${visit.branchCount} Branches` : "-"}</dd>
                      </div>

                      {/* Employee Count */}
                      <div className="space-y-1">
                        <dt className="text-xs lg:text-sm font-medium text-gray-500">Employees Count</dt>
                        <dd className="text-sm lg:text-base text-gray-900 font-semibold">{visit.employeeCount !== null ? `${visit.employeeCount} Employees` : "-"}</dd>
                      </div>

                      {/* Annual Turnover */}
                      <div className="space-y-1">
                        <dt className="text-xs lg:text-sm font-medium text-gray-500">Annual Turnover Range</dt>
                        <dd className="text-sm lg:text-base text-gray-900 font-semibold">{visit.annualTurnover || "-"}</dd>
                      </div>

                      {/* Business Registration */}
                      <div className="space-y-1">
                        <dt className="text-xs lg:text-sm font-medium text-gray-500">Business Registration</dt>
                        <dd>
                          <span className={`inline-flex px-2.5 py-0.5 text-xs font-bold rounded-full ${
                            visit.registrationType === 'registered' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {visit.registrationType === 'registered' ? 'Registered' : 'Unregistered'}
                          </span>
                        </dd>
                      </div>

                      {/* Tax Details if Registered */}
                      {visit.registrationType === 'registered' && (
                        <>
                          <div className="space-y-1">
                            <dt className="text-xs lg:text-sm font-medium text-gray-500">Tax Type</dt>
                            <dd className="text-sm lg:text-base text-gray-900 font-semibold uppercase">{visit.taxType || "GST"}</dd>
                          </div>

                          <div className="space-y-1">
                            <dt className="text-xs lg:text-sm font-medium text-gray-500">Tax Number / GSTIN</dt>
                            <dd className="text-sm lg:text-base text-gray-900 font-mono font-bold">{visit.taxNumber || "-"}</dd>
                          </div>
                        </>
                      )}

                      {/* Preferred Partner Type */}
                      <div className="space-y-1">
                        <dt className="text-xs lg:text-sm font-medium text-gray-500">Preferred Partner Type</dt>
                        <dd className="text-sm lg:text-base text-gray-900 font-semibold">{visit.preferredPartnerType || "-"}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              )}

              {/* 3. BRAWNY & DNBS Only: Student & Candidate Information */}
              {(visit.studentName || visit.age || visit.profession) && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 border-b border-amber-200">
                    <h2 className="text-base lg:text-lg font-semibold text-amber-900 flex items-center">
                      <GraduationCap className="w-5 h-5 mr-2 text-amber-700" />
                      Student & Candidate Details (Brawny & DNBS)
                    </h2>
                  </div>

                  <div className="p-4 lg:p-6">
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                      {visit.studentName && (
                        <div className="space-y-1 bg-amber-50/80 p-3 rounded-lg border border-amber-200 sm:col-span-2">
                          <dt className="text-xs lg:text-sm font-semibold text-amber-900">🎓 Student / Candidate Full Name</dt>
                          <dd className="text-sm lg:text-base text-amber-950 font-bold">
                            {visit.studentName}
                            <span className="text-xs text-amber-700 font-normal ml-2">
                              (Parent/Client: {visit.clientName})
                            </span>
                          </dd>
                        </div>
                      )}

                      {visit.age && (
                        <div className="space-y-1">
                          <dt className="text-xs lg:text-sm font-medium text-gray-500">Age</dt>
                          <dd className="text-sm lg:text-base text-gray-900 font-semibold">{visit.age} Years</dd>
                        </div>
                      )}

                      {visit.profession && (
                        <div className="space-y-1">
                          <dt className="text-xs lg:text-sm font-medium text-gray-500">Profession / Occupation</dt>
                          <dd className="text-sm lg:text-base text-gray-900 font-semibold">💼 {visit.profession}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>
              )}

              {/* 4. Lead Ownership & Audit */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-cyan-50 to-sky-50 px-4 py-3 border-b border-cyan-100">
                  <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
                    <span className="text-lg lg:text-xl mr-2">👥</span>
                    Lead Ownership & Audit
                  </h2>
                </div>

                <div className="p-4 lg:p-6">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Created By */}
                    <div className="bg-gray-50 rounded-lg p-3 lg:p-4">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-2">Created By</dt>
                      <dd>
                        <p className="text-sm lg:text-base font-semibold text-gray-900">
                          {visit.createdBy
                            ? `${visit.createdBy.firstName || ""} ${visit.createdBy.lastName || ""}`.trim()
                            : "-"}
                        </p>
                        {visit.createdBy?.userId && (
                          <p className="text-xs text-gray-500 mt-1 font-mono">ID: {visit.createdBy.userId}</p>
                        )}
                      </dd>
                    </div>

                    {/* Lead Owner */}
                    <div className="bg-blue-50 rounded-lg p-3 lg:p-4 border border-blue-100">
                      <dt className="text-xs lg:text-sm font-medium text-blue-600 mb-2">Lead Owner</dt>
                      <dd>
                        <p className="text-sm lg:text-base font-semibold text-gray-900">
                          {visit.ownerId
                            ? `${visit.ownerId.firstName || ""} ${visit.ownerId.lastName || ""}`.trim()
                            : "-"}
                        </p>
                        {visit.ownerId?.userId && (
                          <p className="text-xs text-gray-500 mt-1 font-mono">ID: {visit.ownerId.userId}</p>
                        )}
                        {visit.ownerId?.customRoleId?.roleName && (
                          <span className="inline-flex mt-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">
                            {visit.ownerId.customRoleId.roleName}
                          </span>
                        )}
                      </dd>
                    </div>

                    {/* Updated By */}
                    <div className="bg-purple-50 rounded-lg p-3 lg:p-4 border border-purple-100">
                      <dt className="text-xs lg:text-sm font-medium text-purple-600 mb-2">Last Updated By</dt>
                      <dd>
                        <p className="text-sm lg:text-base font-semibold text-gray-900">
                          {visit.updatedBy
                            ? `${visit.updatedBy.firstName || ""} ${visit.updatedBy.lastName || ""}`.trim()
                            : "-"}
                        </p>
                        {visit.updatedBy?.userId && (
                          <p className="text-xs text-gray-500 mt-1 font-mono">ID: {visit.updatedBy.userId}</p>
                        )}
                      </dd>
                    </div>

                    {/* Lead Date */}
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Lead Date</dt>
                      <dd className="text-sm lg:text-base text-gray-900 font-semibold">
                        {visit.leadDate
                          ? new Date(visit.leadDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "-"}
                      </dd>
                    </div>

                    {/* Created At */}
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Created At</dt>
                      <dd className="text-sm lg:text-base text-gray-900">
                        {visit.createdAt
                          ? new Date(visit.createdAt).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </dd>
                    </div>

                    {/* Last Updated */}
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Last Updated</dt>
                      <dd className="text-sm lg:text-base text-gray-900">
                        {visit.updatedAt
                          ? new Date(visit.updatedAt).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </dd>
                    </div>

                    {/* Status */}
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Lead Status</dt>
                      <dd>
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full capitalize ${
                            visit.status === "active"
                              ? "bg-green-100 text-green-700"
                              : visit.status === "inactive"
                                ? "bg-gray-100 text-gray-700"
                                : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {visit.status || "-"}
                        </span>
                      </dd>
                    </div>

                    {/* Existing Client */}
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Client Type</dt>
                      <dd>
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full ${
                            visit.isExistingClient
                              ? "bg-green-100 text-green-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {visit.isExistingClient ? "Existing Client" : "New Client"}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              {/* 5. Contact Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3 border-b border-green-100">
                  <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
                    <span className="text-lg lg:text-xl mr-2">👤</span>
                    Contact Details
                  </h2>
                </div>
                <div className="p-4 lg:p-6">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Contact Name</dt>
                      <dd className="text-sm lg:text-base text-gray-900 font-semibold">{visit.contactName}</dd>
                    </div>
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Designation</dt>
                      <dd className="text-sm lg:text-base text-gray-900">{visit.contactDesignation || "-"}</dd>
                    </div>
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Email</dt>
                      <dd className="text-sm lg:text-base">
                        <a
                          href={`mailto:${visit.contactEmail}`}
                          className="text-blue-600 hover:underline break-all font-medium"
                        >
                          {visit.contactEmail || "-"}
                        </a>
                      </dd>
                    </div>
                    <div className="space-y-1">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500">Mobile Phone</dt>
                      <dd className="text-sm lg:text-base">
                        <a
                          href={`tel:${visit.contactPhone}`}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {visit.contactPhone || "-"}
                        </a>
                      </dd>
                    </div>
                    {visit.contactLandline && (
                      <div className="space-y-1">
                        <dt className="text-xs lg:text-sm font-medium text-gray-500">Landline</dt>
                        <dd className="text-sm lg:text-base text-gray-900">{visit.contactLandline}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              {/* 6. Business Details & Enrolled Program */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-4 py-3 border-b border-purple-100 mb-2">
                  <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
                    <span className="text-lg lg:text-xl mr-2">💼</span>
                    Business & Financial Details
                  </h2>
                </div>

                {/* Enrolled Program Banner */}
                {visit.programId && (
                  <div className="mx-4 lg:mx-6 my-2 bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-xl border border-red-200 space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <span className="text-xs font-semibold text-red-600 uppercase tracking-wide block">
                          Enrolled / Inquired Program
                        </span>
                        <h3 className="text-base lg:text-lg font-bold text-gray-900">
                          🎯 {visit.programId?.name || "Program"}
                        </h3>
                      </div>
                      {visit.programId?.code && (
                        <span className="bg-red-100 text-red-800 text-xs px-2.5 py-1 rounded font-mono font-bold">
                          {visit.programId.code}
                        </span>
                      )}
                    </div>

                    {visit.selectedPlanName && (
                      <div className="pt-2 border-t border-red-100 flex items-center gap-2">
                        <span className="text-xs text-gray-500">Selected Plan:</span>
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-white text-gray-800 border border-red-200 shadow-xs">
                          {visit.selectedPlanName}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="p-4 lg:p-6 space-y-4 lg:space-y-5">
                  <div>
                    <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-2">Product Categories</dt>
                    <dd>
                      {visit.productCategory && visit.productCategory.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {visit.productCategory.map((cat, index) => (
                            <span
                              key={index}
                              className="inline-flex px-2.5 py-1 text-xs lg:text-sm bg-purple-100 text-purple-700 rounded-full font-medium"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">No categories listed</span>
                      )}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-2">Requirements</dt>
                    <dd>
                      {visit.requirement && visit.requirement.length > 0 ? (
                        <div className="space-y-2">
                          {visit.requirement.map((req, index) => (
                            <div
                              key={index}
                              className="flex items-start bg-gray-50 rounded-lg p-2 lg:p-3"
                            >
                              <span className="text-green-500 mr-2 flex-shrink-0 font-bold">✓</span>
                              <span className="text-xs lg:text-sm text-gray-700 font-medium">{req}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">No requirements listed</span>
                      )}
                    </dd>
                  </div>

                  {/* 3-Column Financial Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                    <div className="bg-gray-50 rounded-lg p-3 lg:p-4 border border-gray-200">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-1">Invoice Value</dt>
                      <dd className="text-lg lg:text-xl font-bold text-gray-900">
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: visit.currency || "INR",
                        }).format(visit.estimatedValue || 0)}
                      </dd>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-3 lg:p-4 border border-blue-200">
                      <dt className="text-xs lg:text-sm font-medium text-blue-700 mb-1">Approximate / Quoted Value</dt>
                      <dd className="text-lg lg:text-xl font-bold text-blue-600">
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: visit.currency || "INR",
                        }).format(visit.expectedValue || 0)}
                      </dd>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-3 lg:p-4 border border-purple-200">
                      <dt className="text-xs lg:text-sm font-medium text-purple-700 mb-1">Monthly Recurring</dt>
                      <dd className="text-lg lg:text-xl font-bold text-purple-600">
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: visit.currency || "INR",
                        }).format(visit.recurringValue || 0)}
                      </dd>
                    </div>
                  </div>
                </div>
              </div>

              {/* 7. Location Details with Images */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-3 border-b border-orange-100">
                  <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
                    <span className="text-lg lg:text-xl mr-2">📍</span>
                    Location Details
                  </h2>
                </div>
                <div className="p-4 lg:p-6">
                  {visit.address && (
                    <div className="mb-4">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-1">Address</dt>
                      <dd className="text-sm lg:text-base text-gray-900 bg-gray-50 p-3 rounded-lg font-medium">
                        {visit.address}
                      </dd>
                    </div>
                  )}

                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                    {visit.locality && (
                      <div className="space-y-1">
                        <dt className="text-xs font-medium text-gray-500">Locality</dt>
                        <dd className="text-sm text-gray-900 font-semibold">{visit.locality}</dd>
                      </div>
                    )}
                    {visit.district && (
                      <div className="space-y-1">
                        <dt className="text-xs font-medium text-gray-500">District</dt>
                        <dd className="text-sm text-gray-900 font-semibold">{visit.district}</dd>
                      </div>
                    )}
                    {visit.state && (
                      <div className="space-y-1">
                        <dt className="text-xs font-medium text-gray-500">State</dt>
                        <dd className="text-sm text-gray-900 font-semibold">{visit.state}</dd>
                      </div>
                    )}
                    {visit.country && (
                      <div className="space-y-1">
                        <dt className="text-xs font-medium text-gray-500">Country</dt>
                        <dd className="text-sm text-gray-900 font-semibold">{visit.country}</dd>
                      </div>
                    )}
                    {visit.pincode && (
                      <div className="space-y-1">
                        <dt className="text-xs font-medium text-gray-500">Pincode</dt>
                        <dd className="text-sm text-gray-900 font-mono font-semibold">{visit.pincode}</dd>
                      </div>
                    )}
                    {visit.latitude && visit.longitude && (
                      <div className="space-y-1 col-span-2 lg:col-span-1">
                        <dt className="text-xs font-medium text-gray-500">Map Coordinates</dt>
                        <dd className="text-sm text-gray-900 font-medium">
                          <a
                            href={`https://maps.google.com/?q=${visit.latitude},${visit.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                          >
                            View on Map <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </dd>
                      </div>
                    )}
                  </div>

                  {/* Location Images */}
                  {visit.locationImages && visit.locationImages.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-3">Location Images</dt>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {visit.locationImages.map((image, index) => (
                          <a
                            key={index}
                            href={image}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-90 transition-opacity border border-gray-200"
                          >
                            <img
                              src={image}
                              alt={`Location ${index + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Pipeline Status, Quick Actions & Organization */}
            <div className="xl:col-span-1 space-y-4 lg:space-y-6">
              
              {/* Pipeline Status Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 border-b border-indigo-100">
                  <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
                    <span className="text-lg lg:text-xl mr-2">📊</span>
                    Pipeline Status
                  </h2>
                </div>

                <div className="p-4 lg:p-6 space-y-4">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                    <p className="text-xs lg:text-sm text-gray-600 mb-2 font-medium">Current Stage</p>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full shadow-xs flex-shrink-0"
                        style={{
                          backgroundColor: visit.stageId?.color || "#3B82F6",
                        }}
                      ></div>
                      <div>
                        <p className="text-base lg:text-lg font-bold text-gray-900">
                          {visit.stageId?.stageName || "Not Set"}
                        </p>
                        <p className="text-xs lg:text-sm text-gray-500 font-medium">
                          {visit.stageId?.probability || 0}% win probability
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm text-gray-500 font-medium">Payment Status</span>
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full ${
                        visit.paymentStatus === "completed"
                          ? "bg-green-100 text-green-800"
                          : visit.paymentStatus === "partial"
                            ? "bg-yellow-100 text-yellow-800"
                            : visit.paymentStatus === "pending"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {visit.paymentStatus === "na" ? "N/A" : visit.paymentStatus?.toUpperCase()}
                    </span>
                  </div>

                  {(visit.nextFollowUpDate || visit.nextFollowUpTime) && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs lg:text-sm text-gray-500 font-medium">Next Follow-up</span>
                      <span className="text-xs lg:text-sm font-semibold text-blue-600 text-right">
                        {visit.nextFollowUpDate
                          ? new Date(visit.nextFollowUpDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "-"}
                        {visit.nextFollowUpTime && (
                          <span className="block text-gray-500 text-xs">{visit.nextFollowUpTime}</span>
                        )}
                      </span>
                    </div>
                  )}

                  {visit.expectedClosingDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs lg:text-sm text-gray-500 font-medium">Expected Closing</span>
                      <span className="text-xs lg:text-sm font-semibold text-gray-900">
                        {new Date(visit.expectedClosingDate).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  )}

                  {/* Stage Update: Filtered dynamically for this Division & Vertical */}
                  <div className="pt-4 border-t border-gray-100">
                    <p className="font-bold text-gray-700 mb-3 uppercase tracking-wide text-xs">
                      Update Stage (Pipeline):
                    </p>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {salesStages.map((stage) => (
                        <button
                          key={stage._id}
                          onClick={() => {
                            if (visit.stageId?._id !== stage._id) {
                              setStageUpdateModal({ isOpen: true, stage });
                            }
                          }}
                          disabled={visit.stageId?._id === stage._id}
                          className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all flex items-center justify-between group ${
                            visit.stageId?._id === stage._id
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                              : "bg-white border border-gray-200 hover:border-gray-300 hover:shadow-xs text-gray-700"
                          }`}
                        >
                          <div className="flex items-center gap-2 truncate">
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: stage.color }}
                            ></div>
                            <span className="font-semibold truncate">{stage.stageName}</span>
                          </div>
                          <span className="text-xs text-gray-500 font-mono">{stage.probability}%</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Visit Summary Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h2 className="text-base font-semibold text-gray-900 flex items-center">
                    <span className="text-lg mr-2">ℹ️</span>
                    Visit Summary
                  </h2>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
                    <p className="text-2xl font-bold text-blue-600">{visit.totalVisitCount || 1}</p>
                    <p className="text-xs text-gray-600 mt-1 font-medium">Total Visits</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
                    <p className="text-xs lg:text-sm font-bold text-green-700">
                      {new Date(visit.lastVisitDate || visit.createdAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-medium">Last Visit</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h2 className="text-base font-semibold text-gray-900 flex items-center">
                    <span className="text-lg mr-2">⚡</span>
                    Quick Actions
                  </h2>
                </div>
                <div className="p-4 space-y-2">
                  <a
                    href={`mailto:${visit.contactEmail}`}
                    className="flex items-center gap-3 w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors group text-blue-700"
                  >
                    <span className="text-lg group-hover:scale-110 transition-transform">📧</span>
                    <span className="text-sm font-semibold">Send Email</span>
                  </a>
                  <a
                    href={`tel:${visit.contactPhone}`}
                    className="flex items-center gap-3 w-full px-4 py-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors group text-green-700"
                  >
                    <span className="text-lg group-hover:scale-110 transition-transform">📞</span>
                    <span className="text-sm font-semibold">Call Contact</span>
                  </a>
                  <button
                    onClick={() => setShowFollowUpOffcanvas(true)}
                    className="flex items-center gap-3 w-full px-4 py-3 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg transition-colors group font-semibold text-sm"
                  >
                    <span className="text-lg group-hover:scale-110 transition-transform">📅</span>
                    <span>Add Follow-up</span>
                  </button>
                </div>
              </div>

              {/* Organization Info Card */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <span className="text-base mr-1.5">🏢</span>
                  Organization
                </h3>
                <dl className="space-y-2.5 text-xs">
                  <div className="flex justify-between">
                    <dt className="text-gray-500 font-medium">Organization</dt>
                    <dd className="text-gray-700 font-bold">{visit.organizationId?.name || "-"}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 font-medium">Division</dt>
                    <dd className="text-gray-700 font-bold">{visit.divisionId?.name || "-"}</dd>
                  </div>
                  {visit.isExistingClient !== undefined && (
                    <div className="flex justify-between">
                      <dt className="text-gray-500 font-medium">Client Type</dt>
                      <dd className="text-gray-700 font-semibold">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                            visit.isExistingClient
                              ? "bg-green-100 text-green-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {visit.isExistingClient ? "Existing Client" : "New Client"}
                        </span>
                      </dd>
                    </div>
                  )}
                  <div className="pt-2 mt-2 border-t border-gray-200">
                    <div className="flex justify-between">
                      <dt className="text-gray-500 font-medium">Created Date</dt>
                      <dd className="text-gray-700 font-bold">
                        {new Date(visit.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </dd>
                    </div>
                  </div>
                </dl>
              </div>

            </div>
          </div>

          {/* Visit History Timeline: Full Width at the bottom */}
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 px-4 py-3 border-b border-blue-100">
              <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
                <span className="text-lg lg:text-xl mr-2">📅</span>
                Visit & Call History Timeline
              </h2>
            </div>

            <div className="p-4 lg:p-6">
              {visit.visitHistory && visit.visitHistory.length > 0 ? (
                <div className="space-y-4">
                  {visit.visitHistory.map((history, index) => (
                    <div key={index} className="relative">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-xs">
                            <span className="text-xs lg:text-sm font-bold text-white">
                              {history.visitNumber}
                            </span>
                          </div>
                        </div>

                        <div className="flex-1 pb-4">
                          <div className="bg-gray-50 rounded-lg p-3 lg:p-4 border border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-full ${
                                    history.visitType === "inPerson"
                                      ? "bg-green-100 text-green-700"
                                      : history.visitType === "inBound"
                                        ? "bg-purple-100 text-purple-700"
                                        : "bg-blue-100 text-blue-700"
                                  }`}
                                >
                                  {history.visitType === "inPerson"
                                    ? "🤝 In Person"
                                    : history.visitType === "inBound"
                                      ? "📞 In Bound"
                                      : "📞 Out Bound"}
                                </span>
                                {history.createdBy && (
                                  <span className="text-xs text-gray-600 font-medium">
                                    by {history.createdBy.firstName} {history.createdBy.lastName}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-500 font-mono">
                                {new Date(history.createdAt).toLocaleDateString("en-IN")}
                              </span>
                            </div>

                            <p className="text-sm text-gray-700 font-medium">
                              <span className="font-bold text-gray-900">
                                {new Date(history.visitDate).toLocaleDateString("en-IN", {
                                  weekday: "long",
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </span>
                              <span className="text-gray-600"> at {history.visitTime}</span>
                            </p>

                            {history.remarks && (
                              <p className="text-sm text-gray-600 mt-2 bg-white p-2.5 rounded border border-gray-200">
                                {history.remarks}
                              </p>
                            )}

                            {history.nextFollowUpDate && (
                              <p className="text-sm text-blue-600 font-semibold mt-2">
                                Next follow-up: {new Date(history.nextFollowUpDate).toLocaleDateString("en-IN")}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {index < visit.visitHistory.length - 1 && (
                        <div className="absolute left-4 lg:left-5 top-10 w-0.5 h-full bg-gray-200"></div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No visit history available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stage Update Confirmation Modal */}
      <ConfirmationModal
        isOpen={stageUpdateModal.isOpen}
        onClose={() => setStageUpdateModal({ isOpen: false, stage: null })}
        onConfirm={handleStageUpdate}
        title="Update Stage"
        message={`Are you sure you want to update the stage to "${stageUpdateModal.stage?.stageName}"?`}
        confirmText={updatingStage ? "Updating..." : "Update Stage"}
      />

      {/* Priority Toggle Confirmation Modal */}
      <ConfirmationModal
        isOpen={priorityModal}
        onClose={() => setPriorityModal(false)}
        onConfirm={handlePriorityToggle}
        title={visit?.isPriorityFollowUp ? "Remove Priority" : "Mark as Priority"}
        message={
          visit?.isPriorityFollowUp
            ? "Remove this client from priority follow-up list?"
            : "Mark this client as important priority follow-up?"
        }
        confirmText={priorityLoading ? "Saving..." : "Confirm"}
      />

      {/* Follow-up Drawer */}
      <FollowUpForm
        visitId={visit._id}
        clientId={visit.clientId}
        isOpen={showFollowUpOffcanvas}
        onClose={() => setShowFollowUpOffcanvas(false)}
        onSuccess={() => {
          setShowFollowUpOffcanvas(false);
          fetchVisitDetails();
        }}
      />
    </>
  );
};

export default ClientVisitDetail;

// import React, { useState, useEffect, useCallback } from 'react';
// import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
// import FollowUpForm from './FollowUpForm';
// import ClientVisitDetailSkeleton from '../SkeletonComponents/ClientVisitDetailSkeleton';
// import api from '../../common/apiClient';
// import SummaryApi from '../../common/SummaryApi';
// import { toast } from 'react-toastify';
// import { useMemo } from 'react';
// import { FaArrowLeftLong, FaArrowRightLong } from 'react-icons/fa6';
// import { BsFlag, BsFlagFill } from "react-icons/bs";

// // Confirmation Modal Component
// const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel" }) => {
//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
//       <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
//       <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative z-10">
//         <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
//         <p className="text-sm text-gray-600 mb-6">{message}</p>
//         <div className="flex justify-end gap-3">
//           <button
//             onClick={onClose}
//             className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
//           >
//             {cancelText}
//           </button>
//           <button
//             onClick={onConfirm}
//             className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
//           >
//             {confirmText}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// const ClientVisitDetail = () => {
//   const { id, portalName } = useParams();
//   const navigate = useNavigate();
//   const location = useLocation();
//   // const allIds = location.state?.allIds || [];
//   // const currentIndex = allIds.findIndex(i => i === id);
//   const [salesStages, setSalesStages] = useState([]);
//   const [visit, setVisit] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [showFollowUpOffcanvas, setShowFollowUpOffcanvas] = useState(false);
//   const [stageUpdateModal, setStageUpdateModal] = useState({ isOpen: false, stage: null });
//   const [updatingStage, setUpdatingStage] = useState(false);
//   const [navLoading, setNavLoading] = useState(false);
//   const isAnyModalOpen = showFollowUpOffcanvas || stageUpdateModal.isOpen;
//   const allIds = useMemo(() => {
//     return location.state?.allIds || [];
//   }, [location.state]);
//   const [priorityModal, setPriorityModal] = useState(false);

//   const [priorityLoading, setPriorityLoading] = useState(false);
//   const currentIndex = allIds.findIndex(i => i === id);

//   const handleNext = useCallback(() => {
//     if (currentIndex < allIds.length - 1 && !navLoading) {
//     setNavLoading(true);
//       navigate(`/${portalName}/sales/leads/view/${allIds[currentIndex + 1]}`, {
//         state: { allIds }
//       });
//     }
//   }, [currentIndex, allIds, navigate, portalName, navLoading]);

//   const handlePrev = useCallback(() => {
//     if (currentIndex > 0 && !navLoading) {
//     setNavLoading(true);
//       navigate(`/${portalName}/sales/leads/view/${allIds[currentIndex - 1]}`, {
//         state: { allIds }
//       });
//     }
//   }, [currentIndex, allIds, navigate, portalName, navLoading]);

//   // Fetch Sales Stages
//   const fetchSalesStages = useCallback(async () => {
//     try {
//       const response = await api.get(SummaryApi.getAllStages.url);
//       if (response.data.success) {
//         setSalesStages(response.data.data || []);
//       }
//     } catch (error) {
//       console.error('Failed to fetch stages:', error);
//     }
//   }, []);

//   useEffect(() => {
//     fetchSalesStages();
//   }, [fetchSalesStages]);

//   const fetchVisitDetails = useCallback(async () => {
//     try {
//       const response = await api.get(SummaryApi.getOneClientVisit.url(id));
//       if (response.data.success) {
//         setVisit(response.data.data);
//       }
//     } catch (error) {
//       console.error('Error fetching visit details:', error);
//     } finally {
//       setLoading(false);
//       setNavLoading(false);
//     }
//   }, [id]);

//   useEffect(() => {
//     fetchVisitDetails();
//   }, [fetchVisitDetails]);

//   const handleStageUpdate = async () => {
//     if (!stageUpdateModal.stage) return;

//     setUpdatingStage(true);
//     try {
//       const selectedStage = stageUpdateModal.stage;
//       const payload = {
//         stageId: selectedStage._id,
//         remarks: `Stage updated to ${selectedStage.stageName}`
//       };
      
//       const response = await api.put(SummaryApi.updateVisitStage.url(visit._id), payload);

//       if (response.data.success) {
//         fetchVisitDetails();
//         toast.success('Stage updated successfully');
//         setStageUpdateModal({ isOpen: false, stage: null });
//       } else {
//         toast.error('Failed to update stage');
//       }
//     } catch (error) {
//       toast.error('Failed to update stage');
//     } finally {
//       setUpdatingStage(false);
//     }
//   };

//   useEffect(() => {
//     const handleKey = (e) => {
//       if (isAnyModalOpen) return;
//       if (e.key === "ArrowRight") handleNext();
//       if (e.key === "ArrowLeft") handlePrev();
//     };

//     window.addEventListener("keydown", handleKey);
//     return () => window.removeEventListener("keydown", handleKey);
//   }, [handleNext, handlePrev, isAnyModalOpen]);

//   const handlePriorityToggle = async () => {
//     try {
//       setPriorityLoading(true);

//       const response = await api.patch(SummaryApi.togglePriority.url(visit._id));

//       if (response.data.success) {
//         toast.success(response.data.message);

//         fetchVisitDetails();

//         setPriorityModal(false);
//       }
//     } catch (error) {
//       toast.error("Failed to update priority");
//     } finally {
//       setPriorityLoading(false);
//     }
//   };

//   if (loading) {
//     return <ClientVisitDetailSkeleton />;
//   }

//   if (!visit) {
//     return (
//       <div className="text-center py-12">
//         <p className="text-gray-600">Visit not found</p>
//         <button onClick={() => navigate(-1)} className="mt-4 text-blue-600 hover:text-blue-800">
//           Go Back
//         </button>
//       </div>
//     );
//   }

//   return (
//     <>
//       <div className="min-h-screen bg-gray-50">
//         {/* Header - Improved Mobile Responsive */}
//         <div className="bg-red-100 border-b rounded-md">
//           <div className="px-4 py-3">
//             <div className="flex items-center justify-between gap-2">
//               <div className="flex items-center gap-2 min-w-0 flex-1">
//                 <button
//                   onClick={() => navigate(-1)}
//                   className="flex-shrink-0 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
//                   title="Go back"
//                 >
//                   <svg
//                     className="w-5 h-5 text-gray-600"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M15 19l-7-7 7-7"
//                     />
//                   </svg>
//                 </button>
//                 <div className="min-w-0 flex-1">
//                   <h1 className="text-sm sm:text-base font-bold text-gray-900 truncate">
//                     {visit.clientId}
//                   </h1>
//                   <p className="text-xs sm:text-sm text-gray-600 truncate">
//                     {visit.clientName}
//                   </p>
//                 </div>
//               </div>
//               <div className="hidden md:flex items-center gap-2">
//                 {/* PREV */}
//                 <button
//                   onClick={handlePrev}
//                   disabled={currentIndex === 0 || navLoading}
//                   className={`p-2 rounded-md ${
//                     currentIndex === 0 || navLoading
//                       ? "bg-red-100 text-gray-300 cursor-not-allowed border border-gray-300"
//                       : "bg-red-100 hover:bg-white border border-gray-300"
//                   }`}
//                 >
//                   {navLoading ? "..." : <FaArrowLeftLong />}
//                 </button>

//                 {/* NEXT */}
//                 <button
//                   onClick={handleNext}
//                   disabled={currentIndex === allIds.length - 1 || navLoading}
//                   className={`p-2 rounded-lg ${
//                     currentIndex === allIds.length - 1 || navLoading
//                       ? "bg-red-100 text-gray-300 cursor-not-allowed border border-gray-300"
//                       : "bg-red-100 hover:bg-white border border-gray-300"
//                   }`}
//                 >
//                   {navLoading ? "..." : <FaArrowRightLong />}
//                 </button>
//               </div>
//               <div className="flex gap-2 flex-shrink-0">
//                 <button
//                   onClick={() => setShowFollowUpOffcanvas(true)}
//                   className="px-3 py-1.5 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
//                 >
//                   Follow-up
//                 </button>
//                 <Link
//                   to={`/${portalName}/sales/leads/edit/${visit._id}`}
//                   className="px-3 py-1.5 border border-gray-300 text-xs sm:text-sm rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
//                 >
//                   Edit
//                 </Link>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Main Content */}
//         <div className="p-4 mx-auto">
//           <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
//             {/* Left Column - Details */}
//             <div className="xl:col-span-2 space-y-4 lg:space-y-6">
//               {/* Client Information */}
//               <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//                 <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-blue-100">
//                   <div className="flex items-center justify-between">
//                     <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
//                       <span className="text-lg lg:text-xl mr-2">🏢</span>
//                       Client Information
//                     </h2>

//                     <button
//                       onClick={() => setPriorityModal(true)}
//                       className="transition-all duration-200 hover:scale-110"
//                       title="Priority Follow-up"
//                     >
//                       {visit?.isPriorityFollowUp ? (
//                         <BsFlagFill className="text-red-600 text-2xl" />
//                       ) : (
//                         <BsFlag className="text-gray-400 text-2xl hover:text-red-500" />
//                       )}
//                     </button>
//                   </div>
//                 </div>
//                 <div className="p-4 lg:p-6">
//                   <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                         Client Name
//                       </dt>
//                       <dd className="text-sm lg:text-base text-gray-900 font-medium">
//                         {visit.clientName}
//                       </dd>
//                     </div>
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                         Client ID
//                       </dt>
//                       <dd className="text-sm lg:text-base text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block">
//                         {visit.clientId}
//                       </dd>
//                     </div>
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                         Division
//                       </dt>
//                       <dd className="text-sm lg:text-base text-gray-900">
//                         {visit.divisionId?.name || "-"}
//                       </dd>
//                     </div>
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                         Division Base ID
//                       </dt>
//                       <dd className="text-sm lg:text-base text-gray-900 font-mono">
//                         {visit.divisionBaseId || "-"}
//                       </dd>
//                     </div>
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                         Industry
//                       </dt>
//                       <dd className="text-sm lg:text-base text-gray-900">
//                         <span className="font-medium">
//                           {visit.industryType || "-"}
//                         </span>
//                         {visit.industrySubType && (
//                           <span className="text-gray-600 text-xs lg:text-sm block mt-0.5">
//                             {visit.industrySubType}
//                           </span>
//                         )}
//                       </dd>
//                     </div>

//                     {/* Profession / Occupation */}
//                     {visit.profession && (
//                       <div className="space-y-1">
//                         <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                           Profession / Occupation
//                         </dt>
//                         <dd className="text-sm lg:text-base text-gray-900 font-medium">
//                           💼 {visit.profession}
//                         </dd>
//                       </div>
//                     )}

//                     {/* Age */}
//                     {visit.age && (
//                       <div className="space-y-1">
//                         <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                           Age
//                         </dt>
//                         <dd className="text-sm lg:text-base text-gray-900">
//                           {visit.age} Years
//                         </dd>
//                       </div>
//                     )}

//                     {visit.studentName && (
//                       <div className="space-y-1 bg-amber-50 p-2.5 rounded-lg border border-amber-200 sm:col-span-2">
//                         <dt className="text-xs lg:text-sm font-semibold text-amber-900 flex items-center gap-1.5">
//                           🎓 Student / Candidate Name (Minor &lt; 18)
//                         </dt>
//                         <dd className="text-sm lg:text-base text-amber-950 font-bold">
//                           {visit.studentName}
//                           <span className="text-xs text-amber-700 font-normal ml-2">
//                             (Parent/Client: {visit.clientName})
//                           </span>
//                         </dd>
//                       </div>
//                     )}
//                     {/* Our ERP Product & Product Business Category (Visible for CUSO / ERP Deals) */}
//                     {(visit.ourProductId || visit.divisionId?.name?.toLowerCase().includes('cuso')) && (
//                       <>
//                         {/* Our ERP Product */}
//                         <div className="space-y-1 bg-blue-50/70 p-3 rounded-lg border border-blue-200">
//                           <dt className="text-xs lg:text-sm font-medium text-blue-800 flex items-center gap-1.5">
//                             Our ERP Product
//                           </dt>
//                           <dd className="text-sm lg:text-base text-gray-900 font-semibold flex items-center gap-2 flex-wrap">
//                             <span>
//                               {visit.ourProductId?.name || visit.ourProductName || "Not Specified"}
//                             </span>
//                             {visit.ourProductId?.code && (
//                               <span className="text-xs bg-blue-200/80 text-blue-900 px-2 py-0.5 rounded font-mono font-medium">
//                                 {visit.ourProductId.code}
//                               </span>
//                             )}
//                           </dd>
//                         </div>

//                         {/* Product Business Category */}
//                         <div className="space-y-1 bg-indigo-50/70 p-3 rounded-lg border border-indigo-200">
//                           <dt className="text-xs lg:text-sm font-medium text-indigo-800 flex items-center gap-1.5">
//                             Product Business Category
//                           </dt>
//                           <dd className="text-sm lg:text-base text-gray-900 font-semibold flex items-center gap-2 flex-wrap">
//                             <span>
//                               {visit.ourProductBusinessCategoryId?.name || visit.ourProductSubCategory || "Not Specified"}
//                             </span>
//                             {visit.ourProductBusinessCategoryId?.code && (
//                               <span className="text-xs bg-indigo-200/80 text-indigo-900 px-2 py-0.5 rounded font-mono font-medium">
//                                 {visit.ourProductBusinessCategoryId.code}
//                               </span>
//                             )}
//                           </dd>
//                         </div>
//                       </>
//                     )}
//                     {/* Source Type Field */}
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500">Source Type</dt>
//                       <dd className="text-sm lg:text-base text-gray-900 flex items-center gap-2">
//                         <span className="capitalize font-semibold">{visit.sourceType || "Not Specified"}</span>
//                         {visit.sourceType === 'expo' && (
//                           <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded border border-orange-200 flex items-center gap-1">
//                             🎪 {visit.expoPlace} | {visit.expoYear}
//                           </span>
//                         )}
//                       </dd>
//                     </div>

//                     {/* Product Category Field */}
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-1">Product Categories</dt>
//                       <dd className="flex flex-wrap gap-1.5">
//                         {visit.productCategory?.map((cat, index) => (
//                           <span key={index} className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-100 rounded text-xs lg:text-sm">
//                             {cat}
//                           </span>
//                         )) || "-"}
//                       </dd>
//                     </div>

//                     {/* Requirements Field */}
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-1">Requirements</dt>
//                       <dd className="grid grid-cols-1 gap-1">
//                         {visit.requirement?.map((req, index) => (
//                           <div key={index} className="flex items-center gap-2 text-xs lg:text-sm text-gray-700">
//                             <span className="text-green-500">●</span> {req}
//                           </div>
//                         )) || "-"}
//                       </dd>
//                     </div>
//                   </dl>
//                 </div>
//               </div>

//               {/* Lead Ownership & Audit */}
// <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//   <div className="bg-gradient-to-r from-cyan-50 to-sky-50 px-4 py-3 border-b border-cyan-100">
//     <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
//       <span className="text-lg lg:text-xl mr-2">👥</span>
//       Lead Ownership & Audit
//     </h2>
//   </div>

//   <div className="p-4 lg:p-6">
//     <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

//       {/* Created By */}
//       <div className="bg-gray-50 rounded-lg p-3 lg:p-4">
//         <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-2">
//           Created By
//         </dt>

//         <dd>
//           <p className="text-sm lg:text-base font-semibold text-gray-900">
//             {visit.createdBy
//               ? `${visit.createdBy.firstName || ""} ${visit.createdBy.lastName || ""}`.trim()
//               : "-"}
//           </p>

//           {visit.createdBy?.userId && (
//             <p className="text-xs text-gray-500 mt-1">
//               ID: {visit.createdBy.userId}
//             </p>
//           )}
//         </dd>
//       </div>

//       {/* Lead Owner */}
//       <div className="bg-blue-50 rounded-lg p-3 lg:p-4">
//         <dt className="text-xs lg:text-sm font-medium text-blue-600 mb-2">
//           Lead Owner
//         </dt>

//         <dd>
//           <p className="text-sm lg:text-base font-semibold text-gray-900">
//             {visit.ownerId
//               ? `${visit.ownerId.firstName || ""} ${visit.ownerId.lastName || ""}`.trim()
//               : "-"}
//           </p>

//           {visit.ownerId?.userId && (
//             <p className="text-xs text-gray-500 mt-1">
//               ID: {visit.ownerId.userId}
//             </p>
//           )}

//           {visit.ownerId?.customRoleId?.roleName && (
//             <span className="inline-flex mt-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
//               {visit.ownerId.customRoleId.roleName}
//             </span>
//           )}
//         </dd>
//       </div>

//       {/* Updated By */}
//       <div className="bg-purple-50 rounded-lg p-3 lg:p-4">
//         <dt className="text-xs lg:text-sm font-medium text-purple-600 mb-2">
//           Last Updated By
//         </dt>

//         <dd>
//           <p className="text-sm lg:text-base font-semibold text-gray-900">
//             {visit.updatedBy
//               ? `${visit.updatedBy.firstName || ""} ${visit.updatedBy.lastName || ""}`.trim()
//               : "-"}
//           </p>

//           {visit.updatedBy?.userId && (
//             <p className="text-xs text-gray-500 mt-1">
//               ID: {visit.updatedBy.userId}
//             </p>
//           )}
//         </dd>
//       </div>

//       {/* Lead Date */}
//       <div className="space-y-1">
//         <dt className="text-xs lg:text-sm font-medium text-gray-500">
//           Lead Date
//         </dt>

//         <dd className="text-sm lg:text-base text-gray-900">
//           {visit.leadDate
//             ? new Date(visit.leadDate).toLocaleDateString("en-IN", {
//                 day: "2-digit",
//                 month: "short",
//                 year: "numeric",
//               })
//             : "-"}
//         </dd>
//       </div>

//       {/* Created At */}
//       <div className="space-y-1">
//         <dt className="text-xs lg:text-sm font-medium text-gray-500">
//           Created At
//         </dt>

//         <dd className="text-sm lg:text-base text-gray-900">
//           {visit.createdAt
//             ? new Date(visit.createdAt).toLocaleString("en-IN", {
//                 day: "2-digit",
//                 month: "short",
//                 year: "numeric",
//                 hour: "2-digit",
//                 minute: "2-digit",
//               })
//             : "-"}
//         </dd>
//       </div>

//       {/* Last Updated */}
//       <div className="space-y-1">
//         <dt className="text-xs lg:text-sm font-medium text-gray-500">
//           Last Updated
//         </dt>

//         <dd className="text-sm lg:text-base text-gray-900">
//           {visit.updatedAt
//             ? new Date(visit.updatedAt).toLocaleString("en-IN", {
//                 day: "2-digit",
//                 month: "short",
//                 year: "numeric",
//                 hour: "2-digit",
//                 minute: "2-digit",
//               })
//             : "-"}
//         </dd>
//       </div>

//       {/* Status */}
//       <div className="space-y-1">
//         <dt className="text-xs lg:text-sm font-medium text-gray-500">
//           Lead Status
//         </dt>

//         <dd>
//           <span
//             className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full capitalize ${
//               visit.status === "active"
//                 ? "bg-green-100 text-green-700"
//                 : visit.status === "inactive"
//                   ? "bg-gray-100 text-gray-700"
//                   : "bg-yellow-100 text-yellow-700"
//             }`}
//           >
//             {visit.status || "-"}
//           </span>
//         </dd>
//       </div>

//       {/* Existing Client */}
//       <div className="space-y-1">
//         <dt className="text-xs lg:text-sm font-medium text-gray-500">
//           Client Type
//         </dt>

//         <dd>
//           <span
//             className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
//               visit.isExistingClient
//                 ? "bg-green-100 text-green-700"
//                 : "bg-blue-100 text-blue-700"
//             }`}
//           >
//             {visit.isExistingClient ? "Existing Client" : "New Client"}
//           </span>
//         </dd>
//       </div>

//     </dl>
//   </div>
// </div>

//               {/* Contact Details */}
//               <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//                 <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3 border-b border-green-100">
//                   <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
//                     <span className="text-lg lg:text-xl mr-2">👤</span>
//                     Contact Details
//                   </h2>
//                 </div>
//                 <div className="p-4 lg:p-6">
//                   <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                         Contact Name
//                       </dt>
//                       <dd className="text-sm lg:text-base text-gray-900">
//                         {visit.contactName}
//                       </dd>
//                     </div>
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                         Designation
//                       </dt>
//                       <dd className="text-sm lg:text-base text-gray-900">
//                         {visit.contactDesignation || "-"}
//                       </dd>
//                     </div>
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                         Email
//                       </dt>
//                       <dd className="text-sm lg:text-base">
//                         <a
//                           href={`mailto:${visit.contactEmail}`}
//                           className="text-blue-600 hover:text-blue-800 break-all"
//                         >
//                           {visit.contactEmail}
//                         </a>
//                       </dd>
//                     </div>
//                     <div className="space-y-1">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500">
//                         Phone
//                       </dt>
//                       <dd className="text-sm lg:text-base">
//                         <a
//                           href={`tel:${visit.contactPhone}`}
//                           className="text-blue-600 hover:text-blue-800"
//                         >
//                           {visit.contactPhone}
//                         </a>
//                       </dd>
//                     </div>
//                   </dl>
//                 </div>
//               </div>

//               {/* Business Details */}
//               <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//                 <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-4 py-3 border-b border-purple-100 mb-2">
//                   <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
//                     <span className="text-lg lg:text-xl mr-2">💼</span>
//                     Business Details
//                   </h2>
//                 </div>
//                 {visit.programId && (
//                     <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-xl border border-red-200 space-y-2">
//                       <div className="flex items-center justify-between flex-wrap gap-2">
//                         <div>
//                           <span className="text-xs font-semibold text-red-600 uppercase tracking-wide block">
//                             Enrolled / Inquired Program
//                           </span>
//                           <h3 className="text-base lg:text-lg font-bold text-gray-900">
//                             🎯 {visit.programId?.name || "Program"}
//                           </h3>
//                         </div>
//                         {visit.programId?.code && (
//                           <span className="bg-red-100 text-red-800 text-xs px-2.5 py-1 rounded font-mono font-bold">
//                             {visit.programId.code}
//                           </span>
//                         )}
//                       </div>

//                       {visit.selectedPlanName && (
//                         <div className="pt-2 border-t border-red-100 flex items-center gap-2">
//                           <span className="text-xs text-gray-500">Selected Plan:</span>
//                           <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-white text-gray-800 border border-red-200 shadow-xs">
//                             {visit.selectedPlanName}
//                           </span>
//                         </div>
//                       )}
//                     </div>
//                   )}
//                 <div className="p-4 lg:p-6 space-y-4 lg:space-y-5">
//                   <div>
//                     <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-2">
//                       Product Categories
//                     </dt>
//                     <dd>
//                       {visit.productCategory &&
//                       visit.productCategory.length > 0 ? (
//                         <div className="flex flex-wrap gap-2">
//                           {visit.productCategory.map((cat, index) => (
//                             <span
//                               key={index}
//                               className="inline-flex px-2.5 py-1 text-xs lg:text-sm bg-purple-100 text-purple-700 rounded-full font-medium"
//                             >
//                               {cat}
//                             </span>
//                           ))}
//                         </div>
//                       ) : (
//                         <span className="text-gray-500 text-sm">
//                           No categories listed
//                         </span>
//                       )}
//                     </dd>
//                   </div>

//                   <div>
//                     <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-2">
//                       Requirements
//                     </dt>
//                     <dd>
//                       {visit.requirement && visit.requirement.length > 0 ? (
//                         <div className="space-y-2">
//                           {visit.requirement.map((req, index) => (
//                             <div
//                               key={index}
//                               className="flex items-start bg-gray-50 rounded-lg p-2 lg:p-3"
//                             >
//                               <span className="text-green-500 mr-2 flex-shrink-0">
//                                 ✓
//                               </span>
//                               <span className="text-xs lg:text-sm text-gray-700">
//                                 {req}
//                               </span>
//                             </div>
//                           ))}
//                         </div>
//                       ) : (
//                         <span className="text-gray-500 text-sm">
//                           No requirements listed
//                         </span>
//                       )}
//                     </dd>
//                   </div>
//                   <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
//                     {/* Invoice Value (Actual Billed Deal) */}
//                     <div className="bg-gray-50 rounded-lg p-3 lg:p-4">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-1">
//                         Invoice Value
//                       </dt>
//                       <dd className="text-lg lg:text-xl font-bold text-gray-900">
//                         {new Intl.NumberFormat("en-IN", {
//                           style: "currency",
//                           currency: visit.currency || "INR",
//                         }).format(visit.estimatedValue || 0)}
//                       </dd>
//                     </div>

//                     {/* Approximate / Expected Pipeline Target */}
//                     <div className="bg-blue-50 rounded-lg p-3 lg:p-4">
//                       <dt className="text-xs lg:text-sm font-medium text-blue-700 mb-1">
//                         Approximate / Quoted Value
//                       </dt>
//                       <dd className="text-lg lg:text-xl font-bold text-blue-600">
//                         {new Intl.NumberFormat("en-IN", {
//                           style: "currency",
//                           currency: visit.currency || "INR",
//                         }).format(visit.expectedValue || 0)}
//                       </dd>
//                     </div>

//                     {/* Monthly Recurring if any */}
//                     <div className="bg-purple-50 rounded-lg p-3 lg:p-4">
//                       <dt className="text-xs lg:text-sm font-medium text-purple-700 mb-1">
//                         Monthly Recurring
//                       </dt>
//                       <dd className="text-lg lg:text-xl font-bold text-purple-600">
//                         {new Intl.NumberFormat("en-IN", {
//                           style: "currency",
//                           currency: visit.currency || "INR",
//                         }).format(visit.recurringValue || 0)}
//                       </dd>
//                     </div>
//                   </div>
//                   {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
//                     <div className="bg-gray-50 rounded-lg p-3 lg:p-4">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-1">
//                         Estimated Value
//                       </dt>
//                       <dd className="text-lg lg:text-xl font-semibold text-gray-900">
//                         {new Intl.NumberFormat("en-IN", {
//                           style: "currency",
//                           currency: visit.currency || "INR",
//                         }).format(visit.estimatedValue || 0)}
//                       </dd>
//                     </div>
//                     <div className="bg-green-50 rounded-lg p-3 lg:p-4">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-1">
//                         Expected Value
//                       </dt>
//                       <dd className="text-lg lg:text-xl font-bold text-green-600">
//                         {new Intl.NumberFormat("en-IN", {
//                           style: "currency",
//                           currency: visit.currency || "INR",
//                         }).format(visit.expectedValue || 0)}
//                       </dd>
//                     </div>
//                   </div> */}
//                 </div>
//               </div>

//               {/* Location Details with Images */}
//               <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//                 <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-4 py-3 border-b border-orange-100">
//                   <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
//                     <span className="text-lg lg:text-xl mr-2">📍</span>
//                     Location Details
//                   </h2>
//                 </div>
//                 <div className="p-4 lg:p-6">
//                   {visit.address && (
//                     <div className="mb-4">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-1">
//                         Address
//                       </dt>
//                       <dd className="text-sm lg:text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
//                         {visit.address}
//                       </dd>
//                     </div>
//                   )}
//                   <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
//                     {visit.locality && (
//                       <div className="space-y-1">
//                         <dt className="text-xs font-medium text-gray-500">
//                           Locality
//                         </dt>
//                         <dd className="text-sm text-gray-900">
//                           {visit.locality}
//                         </dd>
//                       </div>
//                     )}
//                     {visit.district && (
//                       <div className="space-y-1">
//                         <dt className="text-xs font-medium text-gray-500">
//                           District
//                         </dt>
//                         <dd className="text-sm text-gray-900">
//                           {visit.district}
//                         </dd>
//                       </div>
//                     )}
//                     {visit.state && (
//                       <div className="space-y-1">
//                         <dt className="text-xs font-medium text-gray-500">
//                           State
//                         </dt>
//                         <dd className="text-sm text-gray-900">{visit.state}</dd>
//                       </div>
//                     )}
//                     {visit.country && (
//                       <div className="space-y-1">
//                         <dt className="text-xs font-medium text-gray-500">
//                           Country
//                         </dt>
//                         <dd className="text-sm text-gray-900">
//                           {visit.country}
//                         </dd>
//                       </div>
//                     )}
//                     {visit.pincode && (
//                       <div className="space-y-1">
//                         <dt className="text-xs font-medium text-gray-500">
//                           Pincode
//                         </dt>
//                         <dd className="text-sm text-gray-900">
//                           {visit.pincode}
//                         </dd>
//                       </div>
//                     )}
//                     {visit.latitude && visit.longitude && (
//                       <div className="space-y-1 col-span-2 lg:col-span-1">
//                         <dt className="text-xs font-medium text-gray-500">
//                           Coordinates
//                         </dt>
//                         <dd className="text-sm text-gray-900">
//                           <a
//                             href={`https://maps.google.com/?q=${visit.latitude},${visit.longitude}`}
//                             target="_blank"
//                             rel="noopener noreferrer"
//                             className="text-blue-600 hover:text-blue-800"
//                           >
//                             View on Map
//                           </a>
//                         </dd>
//                       </div>
//                     )}
//                   </div>

//                   {/* Location Images */}
//                   {visit.locationImages && visit.locationImages.length > 0 && (
//                     <div className="mt-4 pt-4 border-t">
//                       <dt className="text-xs lg:text-sm font-medium text-gray-500 mb-3">
//                         Location Images
//                       </dt>
//                       <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
//                         {visit.locationImages.map((image, index) => (
//                           <a
//                             key={index}
//                             href={image}
//                             target="_blank"
//                             rel="noopener noreferrer"
//                             className="aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-90 transition-opacity"
//                           >
//                             <img
//                               src={image}
//                               alt={`Location ${index + 1}`}
//                               className="w-full h-full object-cover"
//                               loading="lazy"
//                             />
//                           </a>
//                         ))}
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>

//             {/* Right Column - Status & Actions */}
//             <div className="xl:col-span-1 space-y-4 lg:space-y-6">
//               {/* Pipeline Status */}
//               <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//                 <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 border-b border-indigo-100">
//                   <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
//                     <span className="text-lg lg:text-xl mr-2">📊</span>
//                     Pipeline Status
//                   </h2>
//                 </div>
//                 <div className="p-4 lg:p-6 space-y-4">
//                   <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4">
//                     <p className="text-xs lg:text-sm text-gray-600 mb-2">
//                       Current Stage
//                     </p>
//                     <div className="flex items-center gap-3">
//                       <div
//                         className="w-4 h-4 rounded-full shadow-sm flex-shrink-0"
//                         style={{
//                           backgroundColor: visit.stageId?.color || "#3B82F6",
//                         }}
//                       ></div>
//                       <div>
//                         <p className="text-base lg:text-lg font-semibold text-gray-900">
//                           {visit.stageId?.stageName || "Not Set"}
//                         </p>
//                         <p className="text-xs lg:text-sm text-gray-500">
//                           {visit.stageId?.probability || 0}% probability
//                         </p>
//                       </div>
//                     </div>
//                   </div>

//                   <div className="flex items-center justify-between">
//                     <span className="text-xs lg:text-sm text-gray-500">
//                       Payment Status
//                     </span>
//                     <span
//                       className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
//                         visit.paymentStatus === "completed"
//                           ? "bg-green-100 text-green-800"
//                           : visit.paymentStatus === "partial"
//                             ? "bg-yellow-100 text-yellow-800"
//                             : visit.paymentStatus === "pending"
//                               ? "bg-red-100 text-red-800"
//                               : "bg-gray-100 text-gray-800"
//                       }`}
//                     >
//                       {visit.paymentStatus === "na"
//                         ? "N/A"
//                         : visit.paymentStatus}
//                     </span>
//                   </div>

//                   {(visit.nextFollowUpDate || visit.nextFollowUpTime) && (
//                     <div className="flex items-center justify-between">
//                       <span className="text-xs lg:text-sm text-gray-500">
//                         Next Follow-up
//                       </span>

//                       <span className="text-xs lg:text-sm font-medium text-blue-600 text-right">
//                         {visit.nextFollowUpDate
//                           ? new Date(visit.nextFollowUpDate).toLocaleDateString("en-IN", {
//                               day: "2-digit",
//                               month: "short",
//                               year: "numeric",
//                             })
//                           : "-"}

//                         {visit.nextFollowUpTime && (
//                           <span className="block text-gray-500">
//                             {visit.nextFollowUpTime}
//                           </span>
//                         )}
//                       </span>
//                     </div>
//                   )}

//                   {visit.expectedClosingDate && (
//                     <div className="flex items-center justify-between">
//                       <span className="text-xs lg:text-sm text-gray-500">
//                         Expected Closing
//                       </span>
//                       <span className="text-xs lg:text-sm font-medium text-gray-900">
//                         {new Date(visit.expectedClosingDate).toLocaleDateString(
//                           "en-IN",
//                           {
//                             day: "2-digit",
//                             month: "short",
//                             year: "numeric",
//                           },
//                         )}
//                       </span>
//                     </div>
//                   )}

//                   {/* Stage Update */}
//                   <div className="pt-4 border-t">
//                     <p className="text-sm font-medium text-gray-700 mb-3">
//                       Update Stage:
//                     </p>
//                     <div className="space-y-2 max-h-64 overflow-y-auto">
//                       {salesStages.map((stage) => (
//                         <button
//                           key={stage._id}
//                           onClick={() => {
//                             if (visit.stageId?._id !== stage._id) {
//                               setStageUpdateModal({ isOpen: true, stage });
//                             }
//                           }}
//                           disabled={visit.stageId?._id === stage._id}
//                           className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all flex items-center justify-between group ${
//                             visit.stageId?._id === stage._id
//                               ? "bg-gray-100 text-gray-400 cursor-not-allowed"
//                               : "bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm text-gray-700"
//                           }`}
//                         >
//                           <div className="flex items-center gap-2">
//                             <div
//                               className="w-3 h-3 rounded-full"
//                               style={{ backgroundColor: stage.color }}
//                             ></div>
//                             <span className="font-medium">
//                               {stage.stageName}
//                             </span>
//                           </div>
//                           <span className="text-xs text-gray-500">
//                             {stage.probability}%
//                           </span>
//                         </button>
//                       ))}
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Visit Summary */}
//               <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//                 <div className="px-4 py-3 border-b bg-gray-50">
//                   <h2 className="text-base font-semibold text-gray-900 flex items-center">
//                     <span className="text-lg mr-2">ℹ️</span>
//                     Visit Summary
//                   </h2>
//                 </div>
//                 <div className="p-4 grid grid-cols-2 gap-3">
//                   <div className="bg-blue-50 rounded-lg p-3 text-center">
//                     <p className="text-2xl font-bold text-blue-600">
//                       {visit.totalVisitCount || 1}
//                     </p>
//                     <p className="text-xs text-gray-600 mt-1">Total Visits</p>
//                   </div>
//                   <div className="bg-green-50 rounded-lg p-3 text-center">
//                     <p className="text-xs lg:text-sm font-medium text-green-600">
//                       {new Date(
//                         visit.lastVisitDate || visit.createdAt,
//                       ).toLocaleDateString("en-IN", {
//                         day: "2-digit",
//                         month: "short",
//                       })}
//                     </p>
//                     <p className="text-xs text-gray-600 mt-1">Last Visit</p>
//                   </div>
//                 </div>
//               </div>

//               {/* Quick Actions */}
//               <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//                 <div className="px-4 py-3 border-b bg-gray-50">
//                   <h2 className="text-base font-semibold text-gray-900 flex items-center">
//                     <span className="text-lg mr-2">⚡</span>
//                     Quick Actions
//                   </h2>
//                 </div>
//                 <div className="p-4 space-y-2">
//                   <a
//                     href={`mailto:${visit.contactEmail}`}
//                     className="flex items-center gap-3 w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors group"
//                   >
//                     <span className="text-lg group-hover:scale-110 transition-transform">
//                       📧
//                     </span>
//                     <span className="text-sm font-medium text-gray-700">
//                       Send Email
//                     </span>
//                   </a>
//                   <a
//                     href={`tel:${visit.contactPhone}`}
//                     className="flex items-center gap-3 w-full px-4 py-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors group"
//                   >
//                     <span className="text-lg group-hover:scale-110 transition-transform">
//                       📞
//                     </span>
//                     <span className="text-sm font-medium text-gray-700">
//                       Call Contact
//                     </span>
//                   </a>
//                   <button
//                     onClick={() => setShowFollowUpOffcanvas(true)}
//                     className="flex items-center gap-3 w-full px-4 py-3 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg transition-colors group"
//                   >
//                     <span className="text-lg group-hover:scale-110 transition-transform">
//                       📅
//                     </span>
//                     <span className="text-sm font-medium">Add Follow-up</span>
//                   </button>
//                 </div>
//               </div>

//               {/* Organization Info */}
//               <div className="bg-gray-50 rounded-xl p-4">
//                 <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
//                   <span className="text-base mr-1.5">🏢</span>
//                   Organization
//                 </h3>
//                 <dl className="space-y-2.5 text-xs">
//                   <div className="flex justify-between">
//                     <dt className="text-gray-500">Organization</dt>
//                     <dd className="text-gray-700 font-medium">
//                       {visit.organizationId?.name || "-"}
//                     </dd>
//                   </div>
//                   <div className="flex justify-between">
//                     <dt className="text-gray-500">Division</dt>
//                     <dd className="text-gray-700">
//                       {visit.divisionId?.name || "-"}
//                     </dd>
//                   </div>
//                   {visit.isExistingClient !== undefined && (
//                     <div className="flex justify-between">
//                       <dt className="text-gray-500">Client Type</dt>
//                       <dd className="text-gray-700">
//                         <span
//                           className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
//                             visit.isExistingClient
//                               ? "bg-green-100 text-green-700"
//                               : "bg-blue-100 text-blue-700"
//                           }`}
//                         >
//                           {visit.isExistingClient ? "Existing" : "New"}
//                         </span>
//                       </dd>
//                     </div>
//                   )}
//                   <div className="pt-2 mt-2 border-t border-gray-200">
//                     <div className="flex justify-between">
//                       <dt className="text-gray-500">Created</dt>
//                       <dd className="text-gray-700">
//                         {new Date(visit.createdAt).toLocaleDateString("en-IN", {
//                           day: "2-digit",
//                           month: "short",
//                           year: "numeric",
//                         })}
//                       </dd>
//                     </div>
//                   </div>
//                 </dl>
//               </div>
//             </div>
//           </div>

//           {/* Visit History - Always at the bottom */}
//           <div className="mt-6 bg-white rounded-xl shadow-sm overflow-hidden">
//             <div className="bg-gradient-to-r from-blue-50 to-cyan-50 px-4 py-3 border-b border-blue-100">
//               <h2 className="text-base lg:text-lg font-semibold text-gray-900 flex items-center">
//                 <span className="text-lg lg:text-xl mr-2">📅</span>
//                 Visit History
//               </h2>
//             </div>
//             <div className="p-4 lg:p-6">
//               {visit.visitHistory && visit.visitHistory.length > 0 ? (
//                 <div className="space-y-4">
//                   {visit.visitHistory.map((history, index) => (
//                     <div key={index} className="relative">
//                       <div className="flex gap-3">
//                         <div className="flex-shrink-0">
//                           <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
//                             <span className="text-xs lg:text-sm font-bold text-white">
//                               {history.visitNumber}
//                             </span>
//                           </div>
//                         </div>
//                         <div className="flex-1 pb-4">
//                           <div className="bg-gray-50 rounded-lg p-3 lg:p-4">
//                             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
//                               <div className="flex items-center gap-2 flex-wrap">
//                                 <span
//                                   className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
//                                     history.visitType === "inPerson"
//                                       ? "bg-green-100 text-green-700"
//                                       : history.visitType === "inBound"
//                                         ? "bg-purple-100 text-purple-700"
//                                           : "bg-blue-100 text-blue-700"
//                                   }`}
//                                 >
//                                   {history.visitType === "inPerson"
//                                     ? "🤝 In Person"
//                                     : history.visitType === "inBound"
//                                       ? "📞 In Bound"
//                                         : "📞 Out Bound"}
//                                 </span>
//                                 {history.createdBy && (
//                                   <span className="text-xs text-gray-600">
//                                     by {history.createdBy.firstName}{" "}
//                                     {history.createdBy.lastName}
//                                   </span>
//                                 )}
//                               </div>
//                               <span className="text-xs text-gray-500">
//                                 {new Date(history.createdAt).toLocaleDateString(
//                                   "en-IN",
//                                 )}
//                               </span>
//                             </div>
//                             <p className="text-sm text-gray-700">
//                               <span className="font-medium">
//                                 {new Date(history.visitDate).toLocaleDateString(
//                                   "en-IN",
//                                   {
//                                     weekday: "long",
//                                     day: "2-digit",
//                                     month: "short",
//                                     year: "numeric",
//                                   },
//                                 )}
//                               </span>
//                               <span className="text-gray-600">
//                                 {" "}
//                                 at {history.visitTime}
//                               </span>
//                             </p>
//                             {history.remarks && (
//                               <p className="text-sm text-gray-600 mt-2 bg-white p-2 rounded border border-gray-200">
//                                 {history.remarks}
//                               </p>
//                             )}
//                             {history.nextFollowUpDate && (
//                               <p className="text-sm text-blue-600 mt-2">
//                                 Next follow-up:{" "}
//                                 {new Date(
//                                   history.nextFollowUpDate,
//                                 ).toLocaleDateString("en-IN")}
//                               </p>
//                             )}
//                           </div>
//                         </div>
//                       </div>
//                       {index < visit.visitHistory.length - 1 && (
//                         <div className="absolute left-4 lg:left-5 top-10 w-0.5 h-full bg-gray-200"></div>
//                       )}
//                     </div>
//                   ))}
//                 </div>
//               ) : (
//                 <div className="text-center py-8 text-gray-500">
//                   <p className="text-sm">No visit history available</p>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Stage Update Confirmation Modal */}
//       <ConfirmationModal
//         isOpen={stageUpdateModal.isOpen}
//         onClose={() => setStageUpdateModal({ isOpen: false, stage: null })}
//         onConfirm={handleStageUpdate}
//         title="Update Stage"
//         message={`Are you sure you want to update the stage to "${stageUpdateModal.stage?.stageName}"?`}
//         confirmText={updatingStage ? "Updating..." : "Update Stage"}
//       />
//       <ConfirmationModal
//         isOpen={priorityModal}
//         onClose={() => setPriorityModal(false)}
//         onConfirm={handlePriorityToggle}
//         title={
//           visit?.isPriorityFollowUp ? "Remove Priority" : "Mark as Priority"
//         }
//         message={
//           visit?.isPriorityFollowUp
//             ? "Remove this client from priority follow-up list?"
//             : "Mark this client as important priority follow-up?"
//         }
//         confirmText={priorityLoading ? "Saving..." : "Confirm"}
//       />
//       {/* Follow-up Offcanvas */}
//       <FollowUpForm
//         visitId={visit._id}
//         clientId={visit.clientId}
//         isOpen={showFollowUpOffcanvas}
//         onClose={() => setShowFollowUpOffcanvas(false)}
//         onSuccess={() => {
//           setShowFollowUpOffcanvas(false);
//           fetchVisitDetails();
//         }}
//       />
//     </>
//   );
// };

// export default ClientVisitDetail;
